import { ChakraV3 } from '@piccolohealth/ui';
import { constructZodLiteralUnionType, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  type RecurrenceOption,
  recurrenceOptions,
} from '../../../components/forms/HookedTaskRecurrenceSelect';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useCreateParticipantTaskTemplateMutation } from '../../../graphql/hooks/useCreateParticipantTaskTemplateMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { ParticipantTaskTemplateForm } from './ParticipantTaskTemplateForm';

type FormValues = {
  name: string;
  labelIds: string[];
  requiresDueDate: boolean;
  recurrence?: RecurrenceOption;
};

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string().min(1, 'Task name is required'),
  requiresDueDate: z.boolean(),
  labelIds: z.array(z.string()),
  recurrence: constructZodLiteralUnionType(recurrenceOptions.map((option) => option.raw)),
});

interface ParticipantNoteCreateModalProps {
  participantId: string;
}

export const ParticipantTaskTemplateCreateModal = createModal<ParticipantNoteCreateModalProps>(
  (props) => {
    const { modal } = props;
    const { errorToast, successToast, organization } = useAppContext();

    const { hide, remove, visible } = modal;

    const mutation = useCreateParticipantTaskTemplateMutation();

    const methods = useForm<FormValues>({
      defaultValues: {
        name: '',
        requiresDueDate: false,
        recurrence: 'none',
        labelIds: [],
      },
      mode: 'all',
      resolver: zodResolver(schema),
    });

    const onSubmit = React.useCallback(
      async (values: FormValues) => {
        try {
          const recurrence =
            values.recurrence === 'none'
              ? undefined
              : recurrenceOptions.find((option) => option.raw === values.recurrence);

          await mutation.mutateAsync({
            organizationId: organization.id,
            request: {
              labelIds: values.labelIds,
              name: values.name,
              requiresDueDate: values.requiresDueDate,
              recurrence: recurrence ? { intervalDays: recurrence.intervalDays } : undefined,
            },
          });
          successToast('Task template created successfully');
          hide();
        } catch (error) {
          errorToast(`Error creating task template: ${error.message}`);
        }
      },
      [errorToast, hide, mutation, organization.id, successToast],
    );

    return (
      <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
        <ChakraV3.Dialog.Backdrop />
        <ChakraV3.Dialog.Positioner>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <ChakraV3.Dialog.Content>
                <ChakraV3.DialogTitleSubtitle
                  title='Create a task template'
                  subtitle='Please complete the following to proceed'
                />
                <ChakraV3.Dialog.Body>
                  <ParticipantTaskTemplateForm />
                </ChakraV3.Dialog.Body>
                <ChakraV3.Dialog.Footer>
                  <ChakraV3.HStack>
                    <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                      Close
                    </ChakraV3.Button>
                    <HookedSubmitButton>Create</HookedSubmitButton>
                  </ChakraV3.HStack>
                </ChakraV3.Dialog.Footer>
              </ChakraV3.Dialog.Content>
            </form>
          </FormProvider>
        </ChakraV3.Dialog.Positioner>
      </ChakraV3.Dialog.Root>
    );
  },
);
